import React, { useRef, useState } from 'react';
import {
  Typography,
  Box,
  Grid,
  Stack,
  Button,
  Chip,
  Divider,
} from '@mui/material';
import ApiClient from '../../../config/api';
import { useDispatch } from 'react-redux';
import { setIsLoading, setSnackBar } from '../../../store/globalSlice';
import { useTheme } from '@mui/material/styles';
import {
  Box1,
  CallOutgoing,
  Routing2,
  Sms,
  ArrowUp2,
  ArrowDown2,
} from 'iconsax-react';
import openMissionDetail from './openMissionDetail';
import openMap from '../../../components/openMap/index';
import openSms from '../../../components/openSms/index';
import openTel, {
  normalizePhoneNumber,
} from '../../../components/openTel/index';
import { getItemCoords } from './missionsMapView';

const SWIPE_THRESHOLD = 88;
const MAX_SWIPE = 140;
const EXIT_DURATION_MS = 320;

const MissionCard = ({
  setMissionNo,
  item,
  index,
  setOpenDetail,
  setSelectedData,
  setSelectedTask,
  setSelectedTaskDetail,
  onMoveToStart,
  onMoveToEnd,
  canReorder = true,
  isFirst = false,
  isLast = false,
}) => {
  const api = ApiClient();
  const dispatch = useDispatch();
  const theme = useTheme();
  const mutedIcon = theme.palette.text.secondary;

  const [offsetX, setOffsetX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [exitOpacity, setExitOpacity] = useState(1);

  const pointerIdRef = useRef(null);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const offsetRef = useRef(0);
  const axisLockRef = useRef(null);
  const animatingRef = useRef(false);

  const handleSelect = () => {
    if (Math.abs(offsetRef.current) > 6 || animatingRef.current) return;
    openMissionDetail({
      api,
      dispatch,
      item,
      index,
      setSelectedData,
      setSelectedTask,
      setSelectedTaskDetail,
      setMissionNo,
      setOpenDetail,
    });
  };

  const resolveMissionCoords = async () => {
    const direct = getItemCoords(item);
    if (direct) return direct;

    const response = await api.get('/api/app/v1/shipito/tasks/details', {
      params: { id: item?.id },
    });
    const detailItems = response?.data?.data?.items || [];

    for (const detailItem of detailItems) {
      const parcel = detailItem?.parcel || detailItem;
      const coords = getItemCoords({
        ...item,
        ...detailItem,
        ...parcel,
        lat:
          detailItem?.lat ??
          parcel?.lat ??
          parcel?.to?.location?.lat ??
          parcel?.from?.location?.lat,
        lng:
          detailItem?.lng ??
          parcel?.lng ??
          parcel?.to?.location?.lon ??
          parcel?.from?.location?.lon,
      });
      if (coords) return coords;
    }

    return null;
  };

  const handleNavigate = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    let coords = getItemCoords(item);
    if (!coords) {
      try {
        dispatch(setIsLoading(true));
        coords = await resolveMissionCoords();
      } catch (error) {
        console.error('Error resolving mission coordinates:', error);
      } finally {
        dispatch(setIsLoading(false));
      }
    }

    if (!coords) {
      dispatch(
        setSnackBar({
          message: 'موقعیت این ماموریت ثبت نشده است.',
          severity: 'error',
        })
      );
      return;
    }

    openMap(coords.lat, coords.lng);
  };

  const handleSendMessage = (event) => {
    event.stopPropagation();
    const phone = String(item?.phoneNumber || '').replace(/\s/g, '');
    if (!phone) return;
    if (openSms(phone)) return;
    window.location.href = `sms:${phone}`;
  };

  const handleCall = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const phone = item?.phoneNumber;
    if (!phone) return;
    if (openTel(phone)) return;
    const normalizedPhone = normalizePhoneNumber(phone);
    window.location.href = `tel:${normalizedPhone}`;
  };

  const rubberBand = (value) => {
    const abs = Math.abs(value);
    if (abs <= MAX_SWIPE) return value;
    const overflow = abs - MAX_SWIPE;
    const damped = MAX_SWIPE + overflow * 0.28;
    return value < 0 ? -damped : damped;
  };

  const resetSwipe = (animated = true) => {
    offsetRef.current = 0;
    if (!animated) {
      setOffsetX(0);
      setIsDragging(false);
      return;
    }
    setIsDragging(false);
    setOffsetX(0);
  };

  const commitMove = async (direction) => {
    if (animatingRef.current) return;

    if ((direction === 'start' && isFirst) || (direction === 'end' && isLast)) {
      resetSwipe();
      return;
    }

    animatingRef.current = true;
    setIsExiting(true);
    setIsDragging(false);

    const exitX =
      direction === 'start' ? -window.innerWidth : window.innerWidth;
    setOffsetX(exitX);
    setExitOpacity(0);

    await new Promise((resolve) => setTimeout(resolve, EXIT_DURATION_MS));

    try {
      if (direction === 'start') {
        await onMoveToStart?.(item);
      } else {
        await onMoveToEnd?.(item);
      }
    } finally {
      offsetRef.current = 0;
      setOffsetX(0);
      setExitOpacity(1);
      setIsExiting(false);
      animatingRef.current = false;
    }
  };

  const onPointerDown = (event) => {
    if (!canReorder || animatingRef.current) return;
    if (event.button != null && event.button !== 0) return;

    pointerIdRef.current = event.pointerId;
    startXRef.current = event.clientX;
    startYRef.current = event.clientY;
    axisLockRef.current = null;
    setIsDragging(true);
  };

  const onPointerMove = (event) => {
    if (!canReorder || animatingRef.current) return;
    if (pointerIdRef.current !== event.pointerId) return;

    const dx = event.clientX - startXRef.current;
    const dy = event.clientY - startYRef.current;

    if (!axisLockRef.current) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      axisLockRef.current = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      if (axisLockRef.current === 'y') {
        resetSwipe(false);
        return;
      }

      try {
        event.currentTarget.setPointerCapture?.(event.pointerId);
      } catch (_) {
        /* ignore */
      }
    }

    if (axisLockRef.current !== 'x') return;

    event.preventDefault();
    const next = rubberBand(dx);
    offsetRef.current = next;
    setOffsetX(next);
  };

  const onPointerUp = (event) => {
    if (pointerIdRef.current !== event.pointerId) return;
    pointerIdRef.current = null;

    try {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    } catch (_) {
      /* ignore */
    }

    if (!canReorder || animatingRef.current || axisLockRef.current !== 'x') {
      resetSwipe();
      axisLockRef.current = null;
      return;
    }

    const dx = offsetRef.current;
    axisLockRef.current = null;

    if (dx <= -SWIPE_THRESHOLD) {
      commitMove('start');
      return;
    }
    if (dx >= SWIPE_THRESHOLD) {
      commitMove('end');
      return;
    }

    resetSwipe();
  };

  const onPointerCancel = () => {
    pointerIdRef.current = null;
    axisLockRef.current = null;
    resetSwipe();
  };

  const startReveal = Math.min(1, Math.max(0, -offsetX / SWIPE_THRESHOLD));
  const endReveal = Math.min(1, Math.max(0, offsetX / SWIPE_THRESHOLD));
  const cardScale = isDragging
    ? 1 - Math.min(0.03, Math.abs(offsetX) / 2000)
    : isExiting
      ? 0.96
      : 1;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        margin: '10px 0px',
        borderRadius: '16px',
        overflow: 'hidden',
        touchAction: canReorder ? 'pan-y' : 'auto',
        userSelect: 'none',
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
    >
      {/* Swipe right → move to end (reveals left side) */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          right: 14,
          top: '50%',
          zIndex: 0,
          pointerEvents: 'none',
          opacity: endReveal,
          transform: `translateY(-50%) scale(${0.72 + endReveal * 0.28})`,
          transition: isDragging
            ? 'none'
            : 'opacity 0.22s ease, transform 0.22s cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={0.75}
          sx={{
            width: 76,
            height: 76,
            borderRadius: '18px',
            color: '#fff',
            background:
              'linear-gradient(145deg, #5b8cff 0%, #2f62e8 55%, #1e4fd0 100%)',
            boxShadow:
              '0 8px 20px rgba(47, 98, 232, 0.28), inset 0 1px 0 rgba(255,255,255,0.25)',
          }}
        >
          <ArrowDown2 size="20" color="#FFFFFF" variant="Bold" />
          <Typography sx={{ fontSize: 11, fontWeight: 700, lineHeight: 1.1 }}>
            آخر لیست
          </Typography>
        </Stack>
      </Box>

      {/* Swipe left → move to start (reveals right side) */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          left: 14,
          top: '50%',
          zIndex: 0,
          pointerEvents: 'none',
          opacity: startReveal,
          transform: `translateY(-50%) scale(${0.72 + startReveal * 0.28})`,
          transition: isDragging
            ? 'none'
            : 'opacity 0.22s ease, transform 0.22s cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={0.75}
          sx={{
            width: 76,
            height: 76,
            borderRadius: '18px',
            color: '#fff',
            background:
              'linear-gradient(145deg, #4ade57 0%, #22c55e 55%, #16a34a 100%)',
            boxShadow:
              '0 8px 20px rgba(22, 163, 74, 0.28), inset 0 1px 0 rgba(255,255,255,0.25)',
          }}
        >
          <ArrowUp2 size="20" color="#FFFFFF" variant="Bold" />
          <Typography sx={{ fontSize: 11, fontWeight: 700, lineHeight: 1.1 }}>
            اول لیست
          </Typography>
        </Stack>
      </Box>

      <Box
        sx={{
          bgcolor: 'background.card',
          p: '12px',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '16px',
          width: '100%',
          position: 'relative',
          zIndex: 1,
          opacity: exitOpacity,
          transform: `translate3d(${offsetX}px, 0, 0) scale(${cardScale})`,
          transition: isDragging
            ? 'none'
            : `transform ${EXIT_DURATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1), opacity ${EXIT_DURATION_MS}ms ease`,
          boxShadow:
            Math.abs(offsetX) > 8 ? '0 10px 28px rgba(0,0,0,0.12)' : 'none',
          willChange: 'transform, opacity',
        }}
      >
        <Grid container>
          <Grid
            item
            xs={12}
            container
            onClick={handleSelect}
            sx={{ cursor: 'pointer' }}
          >
            <Grid
              item
              flexDirection={'row'}
              alignItems={'center'}
              justifyContent={'space-between'}
              xs={12}
              display={'flex'}
              flexWrap={'wrap'}
            >
              <Stack flexDirection={'row'} alignItems={'center'}>
                <Typography
                  variant={'caption'}
                  textAlign={'left'}
                  mt={'2px'}
                  ml={1}
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '18px',
                  }}
                >
                  {item?.fullName}
                </Typography>
              </Stack>
              <Stack
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'row',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    bgcolor: 'primary.main',
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    color: 'primary.contrastText',
                    mr: '10px',
                    pt: '2px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {index + 1}
                </Box>

                <Chip
                  size="medium"
                  variant="outnlined"
                  label={item?.serviceType}
                />
              </Stack>
            </Grid>
            <Divider
              sx={{
                width: '100%',
                marginTop: '10px',
              }}
            />
            <Grid
              item
              flexDirection={'row'}
              alignItems={'center'}
              justifyContent={'space-between'}
              xs={12}
              display={'flex'}
              flexWrap={'wrap'}
              marginTop="10px"
            >
              <Stack
                sx={{
                  flexDirection: 'row',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Box1 size="22" color={mutedIcon} />

                <Typography
                  variant={'caption'}
                  textAlign={'left'}
                  mt={'2px'}
                  ml={1}
                  sx={{
                    fontSize: '16px',
                    fontWeight: 'bold',
                  }}
                >
                  {item?.count} مرسوله
                </Typography>
              </Stack>
            </Grid>
            <Grid
              item
              flexDirection={'row'}
              alignItems={'center'}
              justifyContent={'space-between'}
              xs={12}
              display={'flex'}
              flexWrap={'wrap'}
              marginTop="10px"
            >
              <Stack
                sx={{
                  flexDirection: 'row',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant={'caption'}
                  textAlign={'left'}
                  mt={'2px'}
                  sx={{
                    fontSize: '16px',
                    fontWeight: 'bold',
                  }}
                >
                  {item?.address}
                </Typography>
              </Stack>
            </Grid>
          </Grid>
          <Grid item xs={12} display={'flex'} gap={1} marginTop="10px">
            <Button
              size="large"
              startIcon={<CallOutgoing variant="Bold" color="#FFFFFF" />}
              onClick={handleCall}
              onPointerDown={(event) => event.stopPropagation()}
              sx={{
                flex: 1,
                bgcolor: '#12a000',
                borderRadius: '8px',
                color: '#FFFFFF',
                fontSize: '14px',
                '&:hover': {
                  bgcolor: '#0e8200',
                },
              }}
            >
              تماس
            </Button>
            <Button
              size="large"
              variant="outlined"
              startIcon={
                <Sms variant="Bold" color={theme.palette.text.primary} />
              }
              onClick={handleSendMessage}
              onPointerDown={(event) => event.stopPropagation()}
              sx={{
                flex: 1,
                borderRadius: '8px',
                color: 'text.primary',
                borderColor: 'divider',
                fontSize: '14px',
                bgcolor: 'transparent',
                '&:hover': {
                  bgcolor: 'background.muted',
                  borderColor: 'text.primary',
                },
              }}
            >
              پیامک
            </Button>
          </Grid>
          <Grid item xs={12} display="flex" gap={1} marginTop="10px">
            <Button
              type="button"
              size="large"
              startIcon={<Routing2 variant="Bold" color="#FFFFFF" />}
              onClick={handleNavigate}
              onPointerDown={(event) => event.stopPropagation()}
              sx={{
                bgcolor: '#2061ef',
                borderRadius: '8px',
                color: '#FFFFFF',
                fontSize: '14px',
                '&:hover': {
                  bgcolor: '#194fc4',
                },
              }}
              fullWidth
            >
              مسیریابی
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default MissionCard;
